import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { timer } from 'rxjs';
import { applyRegion, environment } from 'src/environments/environment';
import { take } from "rxjs/operators";
import { ResponseJobInfo } from '../commons/interfaces';
import { ResponseJobResult } from './interfaces';
import { print_duration } from '../commons/utils';

@Component({
  selector: 'app-news-generation-baseball',
  templateUrl: './news-generation-baseball.component.html',
  styleUrls: ['./news-generation-baseball.component.css']
})
export class NewsGenerationBaseballComponent implements OnInit {
  providerInfoForm: FormGroup
  tokenHasError = [false, '']
  hasErrorProcessing = [false, '']
  isProcessingText = false
  showResults = false
  headers = [
    'Resumen',
    '[Ganador] Lanzador abridor',
    '[Ganador] Lanzador cerrador',
    '[Perdedor] Lanzador abridor',
    '[Ganador] Bateadores',
    '[Perdedor] Bateadores',
    'Lanzador ganador',
    'Lanzador perdedor',
    'Posicionamiento',
  ]
  newsGenerated = []
  finalIndexes: number[];
  unifiedText: string[] = []
  calendarGames = []
  threeLetterTeams = {
    pinar: 'pri',
    artemisa: 'art',
    industriales: "ind",
    cienfuegos: "cfg",
    sancti: "ssp",
    camagüey: "cmg",
    holguín: 'hol',
    santiago: 'scu',
    isla: 'ijv',
    mayabeque: "may",
    matanzas: "mtz",
    villa: "vcl",
    ciego: "cav",
    las: "ltu",
    granma: "gra",
    guantánamo: "gtm",
    // Liga Élite
    agricultores: 'agr',
    cafetaleros: 'caf',
    centrales: 'cen',
    ganaderos: 'gan',
    portuarios: 'por',
    tabacaleros: 'tab',
  }
  progressInfo: {
    total: number,
    current: number,
    position: number | null,
    description: string
  };
  httpOptions = {
    headers: new HttpHeaders({
      "Content-Type": "application/json",
    })
  };
  errorMsg = 'Ha ocurrido un error inesperado procesando los datos de la página. \n' +
    'Si al intentarlo nuevamente, el error persiste; contacte al equipo de desarrollo para reportar el problema.'

  @Input()
  query: string;

  constructor(private http: HttpClient, protected $gaService: GoogleAnalyticsService) { }

  ngOnInit(): void {
    this.progressInfo = {
      total: 0,
      current: 0,
      position: null,
      description: ''
    };
    this.providerInfoForm = new FormGroup({
      purl: new FormControl("", [Validators.required]),
      ptoken_compai: new FormControl("", [Validators.required]),
    });
    this.$gaService.pageView("/", "News Generation");
    this.finalIndexes = new Array(4)

    this.getCalendarInfo()
  }

  select_url(id) {
    this.providerInfoForm.setValue(
      {
        purl: 'http://www.beisbolcubano.cu/estadisticas/BoxScore?idJuego=' + id,
        ptoken_compai: this.providerInfoForm.value.ptoken_compai
      })
  }

  async generate_news() {
    this.$gaService.event("generate_baseball_news");

    const url = this.providerInfoForm.value.purl;
    const token = this.providerInfoForm.value.ptoken_compai;

    this.progressInfo = {
      total: 0,
      current: 0,
      position: null,
      description: ''
    };

    if (this.isProcessingText || url === '' || token === '')
      return;

    this.showResults = false
    this.isProcessingText = true
    this.hasErrorProcessing = [false, ''];

    // Enqueue the generation request
    const startTime = Date.now()
    const jobInfo = await this.callNewsGenerationEndpointV2(url, token)
    console.log(jobInfo);
    if (jobInfo === undefined) {
      this.hasErrorProcessing = [true, this.errorMsg]
      this.isProcessingText = false
      print_duration(startTime)
      return
    }

    // Waiting for result
    let response = await this.callJobResultsEndpointV2(jobInfo.job);
    console.log(response)
    while (response.status !== 'FINISHED' && response.status !== 'ERROR') {
      response = await this.callJobResultsEndpointV2(jobInfo.job);
      if (response.status === 'ERROR')
        break
      this.progressInfo.position = response.position;

      if (response.result?.data) {
        this.updateGeneratedTextsInUI(response);
        this.showResults = true
      }

      await timer(3000).pipe(take(1)).toPromise();
    }

    if (this.responseHasErrors(response)) {
      print_duration(startTime)
      return
    }

    this.updateGeneratedTextsInUI(response);

    this.isProcessingText = false
    this.showResults = true
    this.hasErrorProcessing = [false, ''];
    this.tokenHasError = [false, ''];
    print_duration(startTime)
  }

  private responseHasErrors(response) {
    const errorMap = {
      'token-not-provided': 'El token no fue provisto.',
      'token-inactive': 'El token existe pero no está activo.',
      'token-without-points': 'El token existe pero no tiene puntos disponibles.',
      'token-not-exist': 'El token no existe en la base de datos.',
      'no-game-info-founded': 'No se encontró información de algún juego en la URL provista.',
      'url-incorrect-prefix': 'La URL no responde a ninguna estadística de juego de la forma http://www.beisbolcubano.cu/estadisticas/BoxScore.',
      'url-incorrect-game-id': 'El idJuego que aparece en la URL debe ser un número.',
      'error-token-does-not-exist': 'El token no existe en la base de datos.',
      'error-token-none-or-empty': 'El token no puede ser vacío.',
      'error-token-without-points': 'Al token provisto no le quedan puntos. Debe recargarlo para poder usarlo.',
      'error-token-is-inactive': 'El token existe pero está desactivado.',
      'error-communication-api-token-manager': 'Error durante el proceso de verificación',
      'error-generating-news': 'Ha ocurrido un error en el proceso de generación de la noticia. Si al intentarlo nuevamente, el error persiste; contacte al equipo de desarrollo para reportar el problema.'
    }
    const errorFound = response.result === undefined || response.result === null || response.status === 'ERROR'
    if (response.status === 'ERROR') {
      let msg = errorMap[response.result.error]
      if (msg === undefined) {
        console.log('Key not found: ' + response.result.error)
        msg = this.errorMsg
      }
      this.hasErrorProcessing = [true, msg]
    }
    else if (response.result === undefined || response.result === null)
      this.hasErrorProcessing = [true, this.errorMsg]

    if (errorFound)
      this.isProcessingText = false

    return errorFound
  }

  private updateGeneratedTextsInUI(response: ResponseJobResult) {
    if (response === undefined || response.result === undefined || response.result.data === undefined) {
      return
    }
    let newData = response.result.data[0]

    const strNewData = JSON.stringify(newData)
    const strPrevData = JSON.stringify(this.newsGenerated)

    if (strNewData === strPrevData) {
      return
    }
    this.addExtMetadataField(newData)
    // Move Posicionamiento to the end
    newData = this.moveItemToEnd(newData, 4)
    this.newsGenerated = newData

    this.updatedUnifiedText()
  }

  updatedUnifiedText() {
    this.unifiedText = [];
    for (const item of this.newsGenerated) {
      if (item.showInUnified === true) {
        const options = [item.original]
        options.push(...item.paraphrases)
        this.unifiedText.push(options[item.selectedIdx]);
      }
    }
  }

  addExtMetadataField(data: any[]) {
    for (const element of data) {
      element.showInUnified = true;
      element.selectedIdx = 0
    }
  }

  swapItems(data: any[], idx1: number, idx2: number) {
    const tmp = data[idx1]
    data[idx1] = data[idx2]
    data[idx2] = tmp;
  }

  moveItemToEnd(data: any[], idx: number) {
    const res = []
    for (let i = 0; i < data.length; i++) {
      if (i === idx) continue
      res.push(data[i])
    }
    res.push(data[idx])
    return res
  }

  private async callJobResultsEndpointV2(jobId: string) {
    const endpointUrl = environment.appsUrl + 'v2/news_generation_baseball/job_result/' + jobId;
    let res: any
    const urls = []
    urls.push(environment.heroku + 'apps/v2/news_generation_baseball/job_result/' + jobId)
    urls.push(applyRegion(endpointUrl, environment.renderRegions.oregon))
    urls.push(applyRegion(endpointUrl, environment.renderRegions.frankfurt))

    for (const link of urls) {
      try {
        res = await this.http.get(link, this.httpOptions).pipe().toPromise();
        break
      } catch (error) {
        continue
      }
    }

    const jobResponse: ResponseJobResult = {
      result: {
        error: res?.result?.news?.error,
        data: res?.result?.news?.data
      },
      meta: {
        progress: res?.result?.progress
      },
      status: res?.status,
      position: res?.position
    }

    return jobResponse
  }

  private async getCalendarInfo() {
    const imgUrl = "https://www.beisbolcubano.cu/images/logos/45%20x%2045/"
    const endpointUrl = environment.appsUrl + 'news_generation_baseball/calendar';
    let data: any

    const urls = []
    urls.push(environment.heroku + 'apps/news_generation_baseball/calendar')
    urls.push(applyRegion(endpointUrl, environment.renderRegions.oregon))
    urls.push(applyRegion(endpointUrl, environment.renderRegions.frankfurt))

    for (const link of urls) {
      try {
        data = await this.http.get(link, this.httpOptions).pipe().toPromise();
        break
      } catch (error) {
        continue
      }
    }

    this.calendarGames = data?.result
    if (this.calendarGames === undefined) {
      this.calendarGames = []
      return
    }

    for (const game of this.calendarGames) {
      game.concatInfo = [game.gameId, game.subserie, game.local, game.visitor, game.result, game.date, game.stadium]
        .join(' ').toLowerCase()
      game.localImg = imgUrl + this.threeLetterTeams[game.local.split(' ')[0].toLowerCase()] + '.png'
      game.visitorImg = imgUrl + this.threeLetterTeams[game.visitor.split(' ')[0].toLowerCase()] + '.png'
    }
  }

  private async callNewsGenerationEndpointV2(url: string, token: string) {
    const body = { token, url };
    const endpointUrl = environment.appsUrl + 'v2/news_generation_baseball'

    const urls = []
    urls.push(environment.heroku + 'apps/v2/news_generation_baseball')
    urls.push(applyRegion(endpointUrl, environment.renderRegions.oregon))
    urls.push(applyRegion(endpointUrl, environment.renderRegions.frankfurt))

    for (const link of urls) {
      try {
        return await this.http.post<ResponseJobInfo>(link, body, this.httpOptions).pipe().toPromise();
      } catch (error) {
        continue
      }
    }
  }

  myIsNaN(n: number): boolean {
    return isNaN(n)
  }
}
